// src/hooks/usePerformance.ts
import { useEffect, useRef, useState } from 'react';
import { performance as firebasePerformance, trace } from '@/lib/firebase';

export const usePerformanceTrace = (traceName: string, startImmediately = true) => {
  const traceRef = useRef<ReturnType<typeof trace> | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  
  useEffect(() => {
    // Only run in browser environment and if Firebase performance is initialized
    if (typeof window !== 'undefined' && firebasePerformance) {
      try {
        traceRef.current = trace(firebasePerformance, traceName);
        
        if (startImmediately && !isRunning) {
          traceRef.current.start();
          setIsRunning(true);
        }
        
        // Clean up the trace when the component unmounts
        return () => {
          if (traceRef.current && isRunning) {
            try {
              traceRef.current.stop();
              setIsRunning(false);
            } catch (e) {
              console.debug(`[Performance] Error stopping trace ${traceName}:`, e);
            }
          }
        };
      } catch (e) {
        console.debug(`[Performance] Error creating trace ${traceName}:`, e);
      }
    }
  }, [traceName, startImmediately, isRunning]);
  
  const startTrace = () => {
    if (traceRef.current && !isRunning) {
      try {
        traceRef.current.start();
        setIsRunning(true);
      } catch (e) {
        console.debug(`[Performance] Error starting trace ${traceName}:`, e);
      }
    }
  };
  
  const stopTrace = () => {
    if (traceRef.current && isRunning) {
      try {
        traceRef.current.stop();
        setIsRunning(false);
      } catch (e) {
        console.debug(`[Performance] Error stopping trace ${traceName}:`, e);
      }
    }
  };
  
  const recordMetric = (metricName: string, value: number) => {
    // Firebase Performance only accepts integer values for metrics
    const intValue = Math.round(value);
    
    if (traceRef.current && isRunning) {
      try {
        traceRef.current.putMetric(metricName, intValue);
      } catch (e) {
        console.debug(`[Performance] Error recording metric ${metricName} for trace ${traceName}:`, e);
      }
    } else {
      // Fallback for when trace is not available or not running
      console.debug(`[Performance] ${traceName}:${metricName} = ${intValue}`);
    }
  };
  
  return { startTrace, stopTrace, recordMetric, isRunning };
};